/**
 * NewsStoriesModal.jsx — src/components/News/NewsStoriesModal.jsx
 *
 * Сторис-плеер новостей для клиента. Отличия от версии кабинета сотрудников:
 * токен не передаётся (clientAxios/fetch подставляют ClientToken сами),
 * нет режима предпросмотра, z-index выше клиентских модалок.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Button, IconButton, Stack, Typography } from '@mui/joy';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { LayoutContext } from '../../context';
import { APINewsComplete, APINewsDismiss, APINewsOpen, APINewsSlideView } from '../../api';
import { getDeviceType, markNewsSeen } from '../../shared/newsStorage';
import NewsProgressBar from './NewsProgressBar';
import NewsSlideContent from './NewsSlideContent';

const TICK_MS = 60;
const MIN_TRACK_MS = 700;       // порции меньше — не отправляем, это шум
const MAX_TRACK_MS = 120000;    // серверный потолок на одно событие
const HEARTBEAT_MS = 5000;      // как часто досылаем накопленное время
const HOLD_DELAY_MS = 180;      // сколько держать, чтобы поставить на паузу
const SWIPE_PX = 60;

export default function NewsStoriesModal() {
    const { newsStories, setNewsStories } = useContext(LayoutContext);

    const open = !!newsStories?.open;
    const items = useMemo(() => newsStories?.items || [], [newsStories]);

    const [newsIndex, setNewsIndex] = useState(0);
    const [slideIndex, setSlideIndex] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [holding, setHolding] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [tabHidden, setTabHidden] = useState(false);
    const [busy, setBusy] = useState(false);

    const enterTsRef = useRef(0);      // когда слайд появился на экране
    const sentMsRef = useRef(0);       // сколько времени по слайду уже отправлено
    const openedRef = useRef(false);   // сессия просмотра создана на сервере
    const autoDoneRef = useRef(false); // автозачёт по таймеру уже отправлен
    const holdTimerRef = useRef(null);
    const pointerRef = useRef({ x: 0, y: 0, t: 0 });

    const news = items[newsIndex] || null;
    const slides = useMemo(() => news?.slides || [], [news]);
    const slide = slides[slideIndex] || null;

    const durationMs = Math.max(3, slide?.duration_seconds || 15) * 1000;
    const isLastSlide = slideIndex >= slides.length - 1;
    const isFirstSlide = slideIndex === 0;
    const isFull = slide?.layout === 'full_image';
    const accent = news?.accent_color || '#6366f1';

    // Таймер идёт и на последнем слайде: досмотрел до конца — просмотр
    // засчитан автоматически, даже если «Ок» не нажали. После автозачёта
    // таймер останавливается, модалка остаётся открытой.
    const paused = holding || expanded || tabHidden || busy
        || (isLastSlide && autoDoneRef.current);

    // ── Блокировка скролла страницы ──────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Открытие с конкретной истории (из ленты кружков в шапке).
    useEffect(() => {
        if (!open) return;
        const start = Math.min(
            Math.max(newsStories?.startIndex || 0, 0),
            Math.max(items.length - 1, 0),
        );
        setNewsIndex(start);
        setSlideIndex(0);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Пауза при уходе со вкладки ───────────────────────────────────────────
    useEffect(() => {
        const onVisibility = () => setTabHidden(document.hidden);
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    // ── Отправка накопленного времени по слайду ──────────────────────────────
    // Порциями (раз в HEARTBEAT_MS и при уходе со слайда), а не одним событием
    // в конце: иначе время теряется, когда человек просто закрывает вкладку.
    const flushDuration = useCallback((slideId, keepalive = false) => {
        if (!slideId || !enterTsRef.current) return 0;

        const elapsed = Math.min(Date.now() - enterTsRef.current, MAX_TRACK_MS);
        const pending = elapsed - sentMsRef.current;
        if (pending < MIN_TRACK_MS) return 0;

        sentMsRef.current = elapsed;
        APINewsSlideView(slideId, { duration_ms: pending, count_view: false }, keepalive);
        return pending;
    }, []);

    // ── Сброс состояния сессии при смене новости ─────────────────────────────
    useEffect(() => {
        if (!open || !news) return;
        openedRef.current = false;
        autoDoneRef.current = false;
    }, [open, news?.id]);

    // ── Появление слайда = фиксация показа ───────────────────────────────────
    // Сначала дожидаемся ответа на /open/, потом шлём событие слайда: раньше
    // оба запроса уходили одновременно и создавали ДВЕ строки просмотра
    // (одна с глубиной 0/N, вторая с 1/N).
    useEffect(() => {
        if (!open || !slide) return;

        setElapsed(0);
        setExpanded(false);
        enterTsRef.current = Date.now();
        sentMsRef.current = 0;

        let alive = true;
        const currentId = slide.id;

        (async () => {
            if (!openedRef.current) {
                await APINewsOpen(news.id, { device: getDeviceType() });
                openedRef.current = true;
                if (!alive) return;
            }
            APINewsSlideView(currentId, { duration_ms: 0, device: getDeviceType() });
        })();

        return () => {
            alive = false;
            flushDuration(currentId);
        };
    }, [open, slide?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Досыл времени порциями, пока слайд на экране ─────────────────────────
    useEffect(() => {
        if (!open || !slide || paused) return;
        const id = setInterval(() => { flushDuration(slide.id); }, HEARTBEAT_MS);
        return () => clearInterval(id);
    }, [open, slide?.id, paused, flushDuration]);

    // ── Пауза = момент зафиксировать накопленное ─────────────────────────────
    useEffect(() => {
        if (!open || !slide) return;
        if (paused) flushDuration(slide.id);
    }, [paused, open, slide?.id, flushDuration]);

    // ── Предзагрузка следующего слайда ───────────────────────────────────────
    useEffect(() => {
        const next = slides[slideIndex + 1];
        if (!next) return;
        const urls = [next.background_image_url, ...(next.images || []).map(i => i.url)].filter(Boolean);
        urls.forEach((url) => { const img = new Image(); img.src = url; });
    }, [slides, slideIndex]);

    // ── Таймер ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !slide || paused) return;
        const id = setInterval(() => setElapsed(prev => prev + TICK_MS), TICK_MS);
        return () => clearInterval(id);
    }, [open, slide?.id, paused]);

    const goNext = useCallback(() => {
        if (!slides.length) return;
        if (slideIndex < slides.length - 1) setSlideIndex(i => i + 1);
    }, [slideIndex, slides.length]);

    const goPrev = useCallback(() => {
        if (slideIndex > 0) { setSlideIndex(i => i - 1); return; }
        if (newsIndex > 0) {
            const prevNews = items[newsIndex - 1];
            setNewsIndex(newsIndex - 1);
            setSlideIndex(Math.max(0, (prevNews?.slides?.length || 1) - 1));
        }
    }, [slideIndex, newsIndex, items]);

    useEffect(() => {
        if (!open || paused) return;
        if (elapsed < durationMs) return;

        if (!isLastSlide) {
            goNext();
            return;
        }

        // Последний слайд досмотрен до конца — фиксируем просмотр.
        // Модалку не закрываем: человек сам решит, листать назад или закрыть.
        if (!autoDoneRef.current && news) {
            autoDoneRef.current = true;
            flushDuration(slide.id);
            markNewsSeen(news.id);
            APINewsComplete(news.id, { auto: true, device: getDeviceType() });
        }
    }, [elapsed, durationMs, paused, open, isLastSlide, goNext, news, slide?.id, flushDuration]);

    // ── Закрытие ─────────────────────────────────────────────────────────────
    const closePlayer = useCallback((sendDismiss = true) => {
        // Сначала досылаем накопленное время, только потом обнуляем счётчик:
        // раньше enterTsRef обнулялся первым, и последний отрезок терялся.
        if (slide) flushDuration(slide.id, true);

        if (sendDismiss && news) {
            APINewsDismiss(news.id, { device: getDeviceType() }, true);
        }
        enterTsRef.current = 0;
        sentMsRef.current = 0;
        setNewsStories({ open: false, items: [] });
        setNewsIndex(0);
        setSlideIndex(0);
        setElapsed(0);
        setExpanded(false);
    }, [news, setNewsStories]);

    // Обязательную новость нельзя закрыть, не дойдя до последнего слайда
    const canClose = !news?.is_mandatory || isLastSlide;

    // ── Кнопка «Ок» ──────────────────────────────────────────────────────────
    const handleOk = useCallback(async () => {
        if (!news || busy) return;
        setBusy(true);

        // Время уже отправлено событиями слайда — здесь только факт нажатия,
        // иначе последние секунды учтутся дважды.
        flushDuration(slide.id);
        markNewsSeen(news.id);
        await APINewsComplete(news.id, { device: getDeviceType() });

        setBusy(false);

        if (newsIndex < items.length - 1) {
            setNewsIndex(i => i + 1);
            setSlideIndex(0);
            setElapsed(0);
        } else {
            closePlayer(false);
        }
    }, [news, busy, newsIndex, items.length, closePlayer]);

    // ── Досыл времени при закрытии вкладки ───────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const onHide = () => { if (slide) flushDuration(slide.id, true); };
        const onVisibility = () => { if (document.hidden && slide) flushDuration(slide.id, true); };

        // На мобильных Safari pagehide срабатывает не всегда — дублируем
        // событием скрытия страницы, иначе время снова начнёт теряться.
        window.addEventListener('pagehide', onHide);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', onHide);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [open, slide?.id, flushDuration]);

    // ── Клавиатура ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'ArrowRight') { isLastSlide ? handleOk() : goNext(); }
            else if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'Escape' && canClose) closePlayer(true);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, isLastSlide, canClose, goNext, goPrev, handleOk, closePlayer]);

    // ── Удержание для паузы + свайпы ─────────────────────────────────────────
    const onPointerDown = (e) => {
        pointerRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
        holdTimerRef.current = setTimeout(() => setHolding(true), HOLD_DELAY_MS);
    };

    const onPointerUp = (e) => {
        clearTimeout(holdTimerRef.current);
        const wasHolding = holding;
        setHolding(false);

        const dx = e.clientX - pointerRef.current.x;
        const dy = e.clientY - pointerRef.current.y;

        if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
            if (dx < 0) { isLastSlide ? handleOk() : goNext(); } else goPrev();
            return;
        }
        if (dy > SWIPE_PX * 1.6 && Math.abs(dy) > Math.abs(dx)) {
            if (expanded) setExpanded(false);
            else if (canClose) closePlayer(true);
            return;
        }
        if (wasHolding) return; // это было удержание, а не тап
    };

    const onPointerCancel = () => {
        clearTimeout(holdTimerRef.current);
        setHolding(false);
    };

    if (!open || !news || !slide) return null;

    const percent = paused && isLastSlide ? 100 : (elapsed / durationMs) * 100;

    return (
        <AnimatePresence>
            <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => { if (e.target === e.currentTarget && canClose) closePlayer(true); }}
                sx={{
                    position: 'fixed', inset: 0, zIndex: 10015,
                    background: 'rgba(8,8,14,0.74)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                {/* Стрелка «назад» (только ПК) */}
                <IconButton
                    onClick={goPrev}
                    disabled={isFirstSlide && newsIndex === 0}
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        position: 'absolute', left: 'calc(50% - 290px)',
                        background: 'rgba(255,255,255,0.12)', color: '#fff',
                        '&:hover': { background: 'rgba(255,255,255,0.22)' },
                        '&.Mui-disabled': { opacity: 0.25, color: '#fff' },
                    }}
                >
                    <ChevronLeftRoundedIcon />
                </IconButton>

                <Box
                    component={motion.div}
                    key={news.id}
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.25, ease: [0.22, 0.9, 0.3, 1] }}
                    onPointerDown={onPointerDown}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerCancel}
                    onContextMenu={(e) => e.preventDefault()}
                    sx={{
                        position: 'relative',
                        width: { xs: '100%', sm: '420px' },
                        height: { xs: '100dvh', sm: 'min(760px, 92vh)' },
                        borderRadius: { xs: 0, sm: '24px' },
                        overflow: 'hidden',
                        background: isFull ? '#0d0d12' : (slide.background_color || '#fff'),
                        boxShadow: { xs: 'none', sm: '0 24px 80px rgba(0,0,0,0.55)' },
                        touchAction: 'pan-y',
                        userSelect: 'none',
                    }}
                >
                    {/* Слой навигации тапом: левая треть — назад, правая треть — вперёд */}
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex' }}>
                        <Box onClick={goPrev} sx={{ flex: 1 }} />
                        <Box sx={{ flex: 1 }} />
                        <Box onClick={() => (isLastSlide ? null : goNext())} sx={{ flex: 1 }} />
                    </Box>

                    {/* Контент слайда */}
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
                        <Box sx={{ position: 'absolute', inset: 0, '& > *': { pointerEvents: 'auto' } }}>
                            <NewsSlideContent
                                slide={slide}
                                expanded={expanded}
                                onToggleExpand={() => setExpanded(v => !v)}
                                accentColor={accent}
                            />
                        </Box>
                    </Box>

                    <NewsProgressBar
                        total={slides.length}
                        index={slideIndex}
                        percent={percent}
                        dark={!isFull}
                    />

                    {/* Шапка: название новости + крестик */}
                    <Stack
                        direction="row" alignItems="center" justifyContent="space-between"
                        sx={{
                            position: 'absolute',
                            top: 'calc(20px + env(safe-area-inset-top))',
                            left: 0, right: 0, px: 1.6, zIndex: 8,
                        }}
                    >
                        <Typography sx={{
                            fontSize: '0.78rem', fontWeight: 700,
                            color: isFull ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.6)',
                            textShadow: isFull ? '0 1px 6px rgba(0,0,0,0.4)' : 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: '75%',
                        }}>
                            {news.title}
                            {items.length > 1 && ` · ${newsIndex + 1}/${items.length}`}
                        </Typography>

                        {canClose && (
                            <IconButton
                                size="sm"
                                onClick={() => closePlayer(true)}
                                sx={{
                                    minHeight: 32, minWidth: 32,
                                    borderRadius: '50%',
                                    background: 'rgba(0,0,0,0.35)',
                                    color: '#fff',
                                    '&:hover': { background: 'rgba(0,0,0,0.5)' },
                                }}
                            >
                                <CloseRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
                            </IconButton>
                        )}
                    </Stack>

                    {/* Панель кнопок */}
                    <Stack
                        spacing={1}
                        sx={{
                            position: 'absolute', left: 0, right: 0, bottom: 0,
                            zIndex: 9, px: 1.6, pt: 2,
                            pb: 'calc(14px + env(safe-area-inset-bottom))',
                            background: isFull
                                ? 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)'
                                : 'transparent',
                        }}
                    >
                        {slide.button_text && slide.button_url && (
                            <Button
                                fullWidth
                                variant="soft"
                                endDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => window.open(slide.button_url, '_blank', 'noopener')}
                                sx={{
                                    background: isFull ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.05)',
                                    color: isFull ? '#fff' : 'inherit',
                                    backdropFilter: 'blur(8px)',
                                    fontWeight: 700,
                                    '&:hover': { background: isFull ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.1)' },
                                }}
                            >
                                {slide.button_text}
                            </Button>
                        )}

                        <Stack direction="row" spacing={1}>
                            {!isFirstSlide && (
                                <Button
                                    variant="soft"
                                    startDecorator={<ArrowBackRoundedIcon sx={{ fontSize: 17 }} />}
                                    onClick={goPrev}
                                    sx={{
                                        flex: 1,
                                        background: isFull ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.05)',
                                        color: isFull ? '#fff' : 'inherit',
                                        backdropFilter: 'blur(8px)',
                                        fontWeight: 700,
                                        '&:hover': { background: isFull ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.1)' },
                                    }}
                                >
                                    Назад
                                </Button>
                            )}

                            {!isLastSlide && (
                                <Button
                                    endDecorator={<ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />}
                                    onClick={goNext}
                                    sx={{
                                        flex: 2,
                                        fontWeight: 800,
                                        background: accent,
                                        '&:hover': { background: accent, filter: 'brightness(1.08)' },
                                    }}
                                >
                                    Далее
                                </Button>
                            )}

                            {isLastSlide && (
                                <Button
                                    loading={busy}
                                    endDecorator={!busy && <CheckRoundedIcon sx={{ fontSize: 18 }} />}
                                    onClick={handleOk}
                                    sx={{
                                        flex: 2,
                                        fontWeight: 800,
                                        background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
                                        boxShadow: `0 8px 24px ${accent}55`,
                                        '&:hover': { filter: 'brightness(1.08)' },
                                    }}
                                >
                                    {newsIndex < items.length - 1 ? 'Ок, дальше' : 'Ок'}
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {/* Индикатор паузы при удержании */}
                    {holding && (
                        <Box sx={{
                            position: 'absolute', inset: 0, zIndex: 10,
                            background: 'rgba(0,0,0,0.12)', pointerEvents: 'none',
                        }} />
                    )}
                </Box>

                {/* Стрелка «вперёд» (только ПК) */}
                <IconButton
                    onClick={() => (isLastSlide ? handleOk() : goNext())}
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        position: 'absolute', right: 'calc(50% - 290px)',
                        background: 'rgba(255,255,255,0.12)', color: '#fff',
                        '&:hover': { background: 'rgba(255,255,255,0.22)' },
                    }}
                >
                    <ChevronRightRoundedIcon />
                </IconButton>
            </Box>
        </AnimatePresence>
    );
}